import React, { useState } from "react";
import { Select, MenuItem, Slider, Button } from "@mui/material";
import VideoCameraBackIcon from "@mui/icons-material/VideoCameraBack";

const Branding = () => {
    const [logoImage, setLogoImage] = useState(null);
    const [logoPosition, setLogoPosition] = useState("top-left");
    const [logoSize, setLogoSize] = useState(15);
    const [primaryColor, setPrimaryColor] = useState("#ff0000");
    const [logoOpacity, setLogoOpacity] = useState(100);

    const positionClasses = {
        "top-left": "top-2 left-2",
        "top-right": "top-2 right-2",
        "bottom-left": "bottom-2 left-2",
        "bottom-right": "bottom-2 right-2",
    };

    const handleImageUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const imageUrl = URL.createObjectURL(file);
            setLogoImage(imageUrl);
        }
    };

    const handleRemoveImage = () => {
        setLogoImage(null);
    };

    return (
        <div className="space-y-8 mt-[40px] font-[Outfit] text-gray-800">
            {/* Video Preview */}
            <div className="sticky top-0 z-10 bg-[#f9fafb] pt-4">
                <div className="flex justify-center">
                    <div className="relative w-2/3 aspect-video bg-gray-100 rounded-xl flex items-center justify-center border-2 border-dashed border-gray-400">
                        <VideoCameraBackIcon
                            style={{ fontSize: 64, color: primaryColor }}
                        />

                        {/* Logo Container */}
                        <div
                            className={`absolute ${positionClasses[logoPosition]} transition-all duration-300`}
                            style={{ width: `${logoSize}%` }}
                        >
                            {logoImage ? (
                                <img
                                    src={logoImage}
                                    alt="Brand Logo"
                                    className="w-full h-auto object-contain"
                                    style={{ opacity: logoOpacity / 100 }} // Apply opacity
                                />
                            ) : (
                                <div
                                    className="w-full aspect-square bg-gray-400 rounded-lg"
                                    style={{ opacity: logoOpacity / 100 }}
                                />
                            )}
                        </div>
                    </div>
                </div>
                <div className="border-b border-gray-300 my-4" />
            </div>

            {/* Primary Color Picker */}
            <div className="flex justify-between items-center w-full">
                <div className="flex-1">
                    <div className="text-xl font-medium">Primary Color</div>
                    <div className="text-lg text-gray-500 mt-1">
                        Primary color for video elements like text, borders,
                        backgrounds and accents.
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <input
                        type="color"
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        className="hidden"
                        id="color-picker"
                    />
                    <label
                        htmlFor="color-picker"
                        className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-200"
                        style={{ backgroundColor: primaryColor }}
                    />
                </div>
            </div>

            <div className="border-b border-gray-300 my-4" />

            {/* Image Upload Section */}
            <div className="flex justify-between items-center w-full">
                <div className="flex-1">
                    <div className="text-xl font-medium">Brand Logo</div>
                    <div className="text-lg text-gray-500 mt-1">
                        Upload your brand logo to display on videos.
                    </div>
                </div>
                <div className="flex gap-4">
                    <input
                        accept="image/*"
                        style={{ display: "none" }}
                        id="logo-upload"
                        type="file"
                        onChange={handleImageUpload}
                    />
                    <label htmlFor="logo-upload">
                        <Button variant="outlined" component="span">
                            Upload
                        </Button>
                    </label>
                    {logoImage && (
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={handleRemoveImage}
                        >
                            Remove
                        </Button>
                    )}
                </div>
            </div>

            <div className="border-b border-gray-300 my-4" />

            {/* Position Control */}
            <div className="flex justify-between items-center w-full">
                <div className="flex-1">
                    <div className="text-xl font-medium">Logo Position</div>
                    <div className="text-lg text-gray-500 mt-1">
                        Set where the logo appears on your videos.
                    </div>
                </div>
                <Select
                    value={logoPosition}
                    onChange={(e) => setLogoPosition(e.target.value)}
                    className="w-40 text-xl"
                    size="small"
                >
                    <MenuItem value="top-left">Top Left</MenuItem>
                    <MenuItem value="top-right">Top Right</MenuItem>
                    <MenuItem value="bottom-left">Bottom Left</MenuItem>
                    <MenuItem value="bottom-right">Bottom Right</MenuItem>
                </Select>
            </div>

            <div className="border-b border-gray-300 my-4" />

            {/* Size Control */}
            <div className="flex justify-between items-center w-full">
                <div className="flex-1">
                    <div className="text-xl font-medium">Logo Size</div>
                    <div className="text-lg text-gray-500 mt-1">
                        Adjust the size of the logo relative to video (5-30%).
                    </div>
                </div>
                <div className="w-64">
                    <Slider
                        value={logoSize}
                        onChange={(e, newValue) => setLogoSize(newValue)}
                        valueLabelDisplay="auto"
                        min={5}
                        max={30}
                        className="mt-2"
                    />
                </div>
            </div>
            <div className="border-b border-gray-300 my-4" />

            {/* New Opacity Control */}
            <div className="flex justify-between items-center w-full">
                <div className="flex-1">
                    <div className="text-xl font-medium">Logo Opacity</div>
                    <div className="text-lg text-gray-500 mt-1">
                        Adjust the transparency of the logo (0-100%).
                    </div>
                </div>
                <div className="w-64">
                    <Slider
                        value={logoOpacity}
                        onChange={(e, newValue) => setLogoOpacity(newValue)}
                        valueLabelDisplay="auto"
                        min={0}
                        max={100}
                        className="mt-2"
                    />
                </div>
            </div>

            <div className="border-b border-gray-300 my-4" />
        </div>
    );
};

export default Branding;
